import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ClockIcon, CreditCardIcon, FileTextIcon, PaperclipIcon, RulerIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react';
import { permitTypes, poolApplication, poolDocuments, poolRules, profileAddressLabel, type PermitType } from '../../data/permis';
import type { PermisKind } from '../../hooks/usePermisConversation';
interface PermisMessageProps {
  kind: PermisKind;
  live: boolean;
  permitType: PermitType | null;
  projectDetail: string | null;
  documentsAttached: boolean;
  onPickType: (type: PermitType) => void;
  onPickDetail: (detail: string) => void;
  onConfirmRules: () => void;
  onAttachDocuments: () => void;
  onPayFee: () => void;
  onClose: () => void;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const primaryButton = 'inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';

/** Précisions demandées selon le type de permis. */
const detailOptions: Record<string, {
  question: string;
  options: string[];
}> = {
  piscine: {
    question: 'Quel type de piscine prévoyez-vous ?',
    options: ['Piscine creusée', 'Piscine hors-terre', 'Piscine démontable', 'Spa']
  },
  cloture: {
    question: 'Où la clôture sera-t-elle installée ?',
    options: ['Cour arrière', 'Cour latérale', 'En façade']
  },
  renovation: {
    question: 'Quelle pièce sera rénovée ?',
    options: ['Salle de bain', 'Cuisine', 'Sous-sol', 'Autre pièce']
  },
  agrandissement: {
    question: 'Quel type d’agrandissement ?',
    options: ['Rallonge arrière', 'Ajout d’étage', 'Garage attaché', 'Cabanon']
  },
  arbre: {
    question: 'Pourquoi l’arbre doit-il être abattu ?',
    options: ['Arbre malade ou mort', 'Risque pour le bâtiment', 'Projet de construction']
  },
  stationnement: {
    question: 'Quels travaux prévoyez-vous ?',
    options: ['Nouvelle entrée', 'Élargissement', 'Pavage seulement']
  }
};
export function PermisMessage({
  kind,
  live,
  permitType,
  projectDetail,
  documentsAttached,
  onPickType,
  onPickDetail,
  onConfirmRules,
  onAttachDocuments,
  onPayFee,
  onClose
}: PermisMessageProps) {
  if (kind === 'projet') {
    return <div data-id="element-2438" data-mp="35m5y5j3lug05">
        <p className={eyebrow} data-id="element-2439" data-mp="35kl743gi1f71">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-2440" data-mp="6wfczb3vaukl" />
          Demande de permis
        </p>

        {permitType ? <>
            <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2441" data-mp="24e3ty1zxjys">
              Votre projet exige un permis « {permitType.name} ».
            </p>
            <div className="mt-3 rounded-2xl border border-slate-200 bg-ville-sand p-4" data-id="element-2442" data-mp="efyeoykw5cii">
              <p className="text-[13px] text-slate-600" data-id="element-2443" data-mp="jfb0rh3xufk1">{permitType.detail}</p>
              <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-2444" data-mp="jhduoc87osu1">
                <div className="flex items-center gap-1.5" data-id="element-2445" data-mp="nifxf31c7n0r">
                  <CreditCardIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2446" data-mp="sghi1seevuec" />
                  <dt className="sr-only" data-id="element-2447" data-mp="xealwes8scbg">Frais</dt>
                  <dd data-id="element-2448" data-mp="xcziwh6mf8le">Frais : {permitType.fee}</dd>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-2449" data-mp="1273pv0fvzgmh">
                  <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2450" data-mp="174lz6rxtfxv0" />
                  <dt className="sr-only" data-id="element-2451" data-mp="1b6qtzu0030j8">Délai</dt>
                  <dd data-id="element-2452" data-mp="1b8aanlpkjdlj">Délai d’analyse : {permitType.processingTime}</dd>
                </div>
              </dl>
            </div>
            {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2453" data-mp="12ibdyer3nncp">
                <button type="button" onClick={() => onPickType(permitType)} className={primaryButton} data-id="element-2454" data-mp="12ggu73rke1a7">
                  Commencer ma demande
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2455" data-mp="1c7l6twtrpa1v" />
                </button>
                <p className="text-[13px] text-slate-500" data-id="element-2456" data-mp="17al0dkt0a5a8">Ou nommez un autre projet dans le champ.</p>
              </div>}
          </> : <>
            <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2457" data-mp="finf6nhbds8x">Quel projet voulez-vous réaliser ?</p>
            <p className="mt-1 text-sm text-slate-500" data-id="element-2458" data-mp="tioehyhxkrma">
              Je détermine le permis requis et je prépare le dossier avec vous.
            </p>
            {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-2459" data-mp="24xbm4974yhzk">
                {permitTypes.map(type => <li key={type.id} data-id="element-2460" data-mp="1zyh77icahdkq">
                    <button type="button" onClick={() => onPickType(type)} className={chipButton} data-id="element-2461" data-mp="1zxfjada6cwll">
                      {type.name}
                    </button>
                  </li>)}
              </ul>}
          </>}
      </div>;
  }
  if (kind === 'details') {
    const config = permitType ? detailOptions[permitType.id] : undefined;
    return <div data-id="element-2462" data-mp="3dva4k7j4pig5">
        <p className={eyebrow} data-id="element-2463" data-mp="3dv1qwdf6m93w">
          <RulerIcon className="h-4 w-4" aria-hidden="true" data-id="element-2464" data-mp="3dw3efhddwo2i" />
          Précisions sur le projet
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2465" data-mp="5gt6vtqq6qnc">
          {config?.question ?? 'Décrivez brièvement les travaux prévus.'}
        </p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-2466" data-mp="3rt8ff2y8qwqw">
          Projet prévu au {profileAddressLabel} — je reprends l’adresse de votre profil.
        </p>
        {live && config && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-2467" data-mp="od1g18muzlnx">
            {config.options.map(option => <li key={option} data-id="element-2468" data-mp="t52wz7uz20ka">
                <button type="button" onClick={() => onPickDetail(option)} className={chipButton} data-id="element-2469" data-mp="t5cee71yazed">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'conformite') {
    const pool = permitType?.id === 'piscine';
    return <div data-id="element-2470" data-mp="t3nbu06m1s59">
        <p className={eyebrow} data-id="element-2471" data-mp="o2r4fcn1vnv3">
          <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-2472" data-mp="o1pk0hk59p0p" />
          Vérification du règlement
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2473" data-mp="k2p354l88wgc">
          {pool ? `Voici ce que le règlement exige pour une ${(projectDetail ?? 'piscine').toLowerCase()}.` : 'Voici les exigences applicables à votre projet.'}
        </p>

        <ul className="mt-3 space-y-2" data-id="element-2474" data-mp="5crkf462gyyj">
          {poolRules.map(rule => <li key={rule.id} className="rounded-2xl border border-slate-200 bg-ville-sand px-4 py-3" data-id="element-2475" data-mp="d560rei5co5">
              <p className="text-sm font-bold text-ville-navy" data-id="element-2476" data-mp="3sh7hfo1rh88e">{rule.label}</p>
              <p className="mt-0.5 text-[13px] text-slate-600" data-id="element-2477" data-mp="3sjbizzc6tv5i">{rule.detail}</p>
            </li>)}
        </ul>

        <p className="mt-3 text-[13px] text-slate-500" data-id="element-2478" data-mp="2c08h9i4mx55x">
          Une inspectrice vérifiera ces éléments sur place. Un projet non conforme retarde l’émission du permis.
        </p>

        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2479" data-mp="2lyfxfyr3nj6v">
            <button type="button" onClick={onConfirmRules} className={primaryButton} data-id="element-2480" data-mp="2lwlgswmv2d8l">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-2481" data-mp="1ocquk1088x9t" />
              Mon projet respecte ces règles
            </button>
            <p className="text-[13px] text-slate-500" data-id="element-2482" data-mp="1y93kcnsniqza">Un doute ? Écrivez-moi votre situation.</p>
          </div>}
      </div>;
  }
  if (kind === 'documents') {
    return <div data-id="element-2483" data-mp="231n2ly2aaaaf">
        <p className={eyebrow} data-id="element-2484" data-mp="15kg08vjn27nb">
          <PaperclipIcon className="h-4 w-4" aria-hidden="true" data-id="element-2485" data-mp="15jem2i7bt51d" />
          Documents requis
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2486" data-mp="1aihi5d5bw0x7">
          Il manque deux documents à votre dossier.
        </p>

        <ul className="mt-3 space-y-2" data-id="element-2487" data-mp="3yazce2j1fww">
          {poolDocuments.map(document => {
          const done = document.received || documentsAttached;
          return <li key={document.id} className="flex items-start gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-2488" data-mp="cxoarca7oek9">
                <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${done ? 'bg-ville-mist text-ville-deep' : 'bg-slate-200 text-slate-500'}`} aria-hidden="true" data-id="element-2489" data-mp="7wh3debzdhe4">
                  <CheckIcon className="h-3 w-3" data-id="element-2490" data-mp="vmuy5cly9uce" />
                </span>
                <div className="min-w-0" data-id="element-2491" data-mp="rlj50x3vaqzg">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-2492" data-mp="rnvwq1zfgfpp">{document.name}</p>
                  <p className="mt-0.5 text-[13px] text-slate-600" data-id="element-2493" data-mp="15m2dg5c8vz25">{document.detail}</p>
                </div>
              </li>;
        })}
        </ul>

        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2494" data-mp="1k9kgz0e2mnmz">
            <button type="button" onClick={onAttachDocuments} className={primaryButton} data-id="element-2495" data-mp="1p7vsrgnxf87u">
              <PaperclipIcon className="h-4 w-4" aria-hidden="true" data-id="element-2496" data-mp="1aabqihiezr5d" />
              Joindre mes documents
            </button>
            <p className="text-[13px] text-slate-500" data-id="element-2497" data-mp="1f84wb9ft2j54">Photos ou PDF acceptés.</p>
          </div>}
      </div>;
  }
  if (kind === 'frais') {
    return <div data-id="element-2498" data-mp="168hwut6mznep">
        <p className={eyebrow} data-id="element-2499" data-mp="168zzq8nt0zvl">
          <CreditCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-2500" data-mp="1699ppw8un71a" />
          Frais et suite du dossier
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2501" data-mp="rlewmeqxj0no">
          Votre dossier est complet. Aucun montant n’est exigé maintenant.
        </p>

        <dl className="mt-3 rounded-2xl border border-slate-200 bg-ville-sand p-4 text-sm" data-id="element-2502" data-mp="iidt8ltg7nnp">
          <div className="flex items-center justify-between gap-4" data-id="element-2503" data-mp="ml0mmr5rpgn0">
            <dt className="text-slate-600" data-id="element-2504" data-mp="mmbqss34kqen">{permitType?.name ?? 'Permis'}</dt>
            <dd className="font-extrabold text-ville-navy" data-id="element-2505" data-mp="1s4idu7dgaww9">{permitType?.fee ?? '—'}</dd>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4 border-t border-slate-200 pt-2" data-id="element-2506" data-mp="1x67sry3snupm">
            <dt className="text-slate-600" data-id="element-2507" data-mp="1j782km0mv7rc">Payables</dt>
            <dd className="text-slate-600" data-id="element-2508" data-mp="1n5yt2aalfr5h">à l’acceptation de la demande</dd>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4" data-id="element-2509" data-mp="2aw1foavqo4wp">
            <dt className="text-slate-600" data-id="element-2510" data-mp="2fvebvm8i2oby">Analyse</dt>
            <dd className="text-slate-600" data-id="element-2511" data-mp="2fuw884jid7u0">{permitType?.processingTime ?? '—'}</dd>
          </div>
        </dl>
        <p className="mt-2 text-[13px] text-slate-500" data-id="element-2512" data-mp="223r52sgxlxex">
          Si la Ville accepte votre demande, je vous avise et vous payez en ligne ou au comptoir. Le permis est
          émis au paiement.
        </p>

        {live && <button type="button" onClick={onPayFee} className={`mt-4 ${primaryButton}`} data-id="element-2513" data-mp="2ylvq6lsl5hds">
            Déposer ma demande
          </button>}
      </div>;
  }
  return <div data-id="element-2514" data-mp="n4txomfwctad">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-2515" data-mp="n4k6wwn12i3m">
        <CheckIcon className="h-6 w-6" data-id="element-2516" data-mp="wvojxqt57svt" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2517" data-mp="wykgpbt98bio">Demande de permis déposée</h3>
      <p className="mt-1 text-[15px] text-slate-600" data-id="element-2518" data-mp="482jusj4j6dp">
        {permitType?.name ?? 'Permis'} · {projectDetail ?? 'projet résidentiel'} au {profileAddressLabel}.
        Référence {poolApplication.reference}.
      </p>
      <p className="mt-2 flex items-start gap-1.5 text-[13px] text-slate-600" data-id="element-2519" data-mp="e6rq1o1nr2e4">
        <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2520" data-mp="975cfdhx3k4s" />
        Analyse en {permitType?.processingTime ?? '15 jours ouvrables'}. Les frais de{' '}
        {permitType?.fee ?? '—'} seront à payer seulement si la demande est acceptée, et les travaux ne peuvent
        pas commencer avant l’émission du permis.
      </p>

      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-2521" data-mp="1rrlcqkck4kcc">
          <Link to={`/mes-permis/${poolApplication.id}`} className={primaryButton} data-id="element-2522" data-mp="1wnk9av81h8nu">
            Suivre ma demande
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2523" data-mp="21o7xl94vvbed" />
          </Link>
          <button type="button" onClick={onClose} className={`inline-flex items-center gap-2 ${chipButton}`} data-id="element-2524" data-mp="26lq4ktzcbgjq">
            <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-2525" data-mp="190kgi42tn2wh" />
            Demander un autre permis
          </button>
        </div>}
    </div>;
}